<template>
  <div v-if="!hasPermission('List Place Supplier Transfer Value')">Você não tem permissão para acessar esta página!</div>
  <div v-else class="supplier-transfer-page">
    <Toolbar />

    <div class="container-xxl py-6 py-lg-8">
      <!-- Cabeçalho + filtro global -->
      <div class="card shadow-sm border-0 mb-6" v-if="transferTables.length">
        <div class="card-header border-0 pt-6 pb-0">
          <div class="d-flex flex-column flex-lg-row align-items-lg-start justify-content-lg-between gap-4">
            <div class="flex-grow-1">
              <h2 class="fw-bold text-gray-900 mb-2">Valores de repasse</h2>
              <p class="text-muted mb-0 fs-6">
                Lançamento de valores por prestador e por tipo de exame ou catálogo. Tudo que você salvar usa a
                <strong>tabela de repasse</strong> escolhida abaixo, em todas as abas.
              </p>
            </div>
            <div class="col-lg-5 col-xl-4">
              <label class="form-label fw-semibold text-gray-700 mb-2" for="supplier-transfer-table-select">
                <i class="bi bi-table me-1 text-primary"></i>
                Tabela de repasse
              </label>
              <select
                id="supplier-transfer-table-select"
                class="form-select form-select-solid form-select-lg"
                v-model.number="selectedTransferTableId"
                @change="onTransferTableChange"
              >
                <option v-for="t in transferTables" :key="t.id_transfer_table" :value="t.id_transfer_table">
                  {{ t.transfer_table_name }}
                </option>
              </select>
              <p v-if="selectedTransferTableLabel" class="text-muted small mt-2 mb-0">
                <i class="bi bi-check2-circle text-success me-1"></i>
                Grade usando: <strong>{{ selectedTransferTableLabel }}</strong>
              </p>
            </div>
          </div>
        </div>
        <div class="card-body pt-4 pb-6">
          <div class="rounded-3 bg-light-primary bg-opacity-50 border border-primary border-opacity-25 px-4 py-3 d-flex gap-3">
            <i class="bi bi-lightbulb text-primary fs-3 flex-shrink-0 lh-1"></i>
            <div class="fs-7 text-gray-700">
              Em <strong>cada aba</strong>, escolha os prestadores que recebem repasse naquele tipo de exame ou catálogo.
              Cada aba tem sua própria grade e valores; o filtro de tabela acima vale para todas.
            </div>
          </div>
        </div>
      </div>

      <div v-if="!loaded" class="card shadow-sm border-0">
        <div class="card-body d-flex flex-column align-items-center justify-content-center py-15">
          <div class="spinner-border text-primary mb-4" role="status">
            <span class="visually-hidden">Carregando...</span>
          </div>
          <p class="text-muted mb-0">Carregando tabelas e valores de repasse…</p>
        </div>
      </div>

      <template v-else>
        <div v-if="transferTables.length && !selectedTransferTableId" class="alert alert-warning d-flex align-items-start gap-3">
          <i class="bi bi-exclamation-triangle fs-4 flex-shrink-0 mt-1"></i>
          <div>Selecione uma tabela de repasse para carregar a grade.</div>
        </div>

        <div v-if="selectedTransferTableId && visibleKinds.length === 0" class="alert alert-warning d-flex align-items-start gap-3">
          <i class="bi bi-inbox fs-4 flex-shrink-0 mt-1"></i>
          <div v-if="restrictTransferTabsToCatalogExamsOnly">
            Nesta tabela de repasse, exibimos apenas laboratoriais e ocupacionais. Não há itens configurados para essas categorias —
            cadastre em <strong>Exames / Procedimentos</strong>.
          </div>
          <div v-else>
            Não há tipos de exame/procedimento com especialidades neste local nem abas de catálogo para exibir.
            Configure em <strong>Exames / Procedimentos</strong> (incluindo laboratoriais e ocupacionais, se aplicável).
          </div>
        </div>

        <div class="card shadow-sm border-0" v-if="selectedTransferTableId && visibleKinds.length > 0">
          <div class="card-body pt-6 pb-8">
            <ul class="nav nav-pills nav-pills-custom mb-8 flex-wrap gap-2">
              <li class="nav-item mb-2" v-for="(kind, index) in visibleKinds" :key="kindTabKey(kind)">
                <a
                  class="nav-link position-relative d-flex flex-column flex-center overflow-hidden min-h-45px px-4 py-3 rounded-3"
                  :class="{ active: kindIndexSelected === index }"
                  href="#"
                  @click.prevent="setKindSelected(index)"
                >
                  <span class="nav-text text-gray-700 fw-bold fs-6 lh-1">{{ kind.kind_name }}</span>
                  <span class="bullet-custom position-absolute bottom-0 w-100 h-4px bg-primary rounded-bottom"></span>
                </a>
              </li>
            </ul>
            <div v-if="kindIndexSelected !== null && visibleKinds[kindIndexSelected]" class="row g-4 mb-6">
              <div class="col-md-6 col-xl-5">
                <label class="form-label fw-semibold text-gray-700">
                  <i class="bi bi-search me-1 text-gray-500"></i>
                  Buscar na aba
                </label>
                <div class="input-group input-group-solid">
                  <span class="input-group-text border-0 bg-gray-100">
                    <i class="bi bi-funnel text-gray-500"></i>
                  </span>
                  <input
                    type="text"
                    class="form-control form-control-solid border-0"
                    v-model="searchTerm"
                    :placeholder="searchPlaceholder"
                  />
                </div>
              </div>
              <div class="col-md-6 col-xl-7">
                <label class="form-label fw-semibold text-gray-700" for="supplier-transfer-tab-multiselect">
                  <i class="bi bi-people me-1 text-gray-500"></i>
                  Prestadores nesta aba
                </label>
                <MultiSelect
                  id="supplier-transfer-tab-multiselect"
                  v-model="tabSuppliersSelection"
                  :options="availableSuppliers"
                  dataKey="id_supplier"
                  :optionLabel="supplierMultiSelectLabel"
                  appendTo="self"
                  display="chip"
                  :showToggleAll="false"
                  :virtualScrollerOptions="{ itemSize: 44 }"
                  filter
                  :filterFields="['supplier_name']"
                  filterPlaceholder="Buscar prestador"
                  placeholder="Selecione os prestadores para esta aba"
                  :maxSelectedLabels="6"
                  :disabled="
                    !selectedTransferTableId ||
                    !hasPermission('Update Place Supplier Transfer Value') ||
                    !availableSuppliers.length ||
                    tabSuppliersUpdating
                  "
                  class="form-control form-control-solid w-100 d-flex align-items-center justify-content-end supplier-transfer-multiselect"
                  @update:modelValue="onTabSuppliersChange"
                >
                  <template #option="slotProps">
                    <span>{{ supplierMultiSelectLabel(slotProps.option) }}</span>
                  </template>
                </MultiSelect>
                <p v-if="!availableSuppliers.length" class="text-muted small mt-2 mb-0">
                  Cadastre fornecedores em <strong>Financeiro</strong> para habilitar a seleção.
                </p>
              </div>
            </div>

            <div
              v-if="kindIndexSelected !== null && visibleKinds[kindIndexSelected] && !(visibleKinds[kindIndexSelected].suppliers || []).length"
              class="alert alert-light-primary border border-primary border-opacity-25 d-flex align-items-start gap-3 mb-0"
            >
              <i class="bi bi-person-plus fs-4 text-primary flex-shrink-0"></i>
              <div>Inclua ao menos um prestador nesta aba para lançar valores de repasse na grade abaixo.</div>
            </div>

            <div v-if="kindIndexSelected !== null && visibleKinds[kindIndexSelected]" class="tab-content mt-6">
              <div class="supplier-transfer-grid-wrap rounded-3 border bg-white" v-if="!isCatalogKind(visibleKinds[kindIndexSelected])">
                <div class="table-responsive">
                  <table class="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-3 mb-0 supplier-transfer-table">
                  <thead>
                    <tr class="fs-7 fw-bolder text-gray-500 border-bottom-0">
                      <th rowspan="2" class="ps-0 min-w-140px">Exame / procedimento</th>
                      <th
                        v-for="(supplier, sIndex) in visibleKinds[kindIndexSelected].suppliers"
                        :key="'h-' + supplier.id_supplier"
                        class="text-center min-w-140px"
                        :colspan="showInsuranceValue ? 2 : 1"
                        :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                      >
                        <span
                          class="text-truncate d-inline-block max-w-125px"
                          :title="supplierNameHeaderTitle(supplier)"
                        >
                          {{ supplier.supplier_name }}
                          <span v-if="supplier.supplier_active === false" class="badge badge-sm bg-secondary ms-1">Inativo</span>
                        </span>
                      </th>
                    </tr>
                    <tr class="fs-7 fw-bolder text-gray-500 border-bottom-2">
                      <template v-for="(supplier, sIndex) in visibleKinds[kindIndexSelected].suppliers" :key="'h2-' + supplier.id_supplier">
                        <th
                          v-if="showInsuranceValue"
                          class="ps-0 text-center"
                          style="width: 150px;"
                          :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                        >
                          Base convênio
                        </th>
                        <th
                          v-if="showInsuranceValue"
                          class="ps-0 text-center"
                          style="width: 150px;"
                          :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                        >
                          Valor repasse
                        </th>
                        <th
                          v-if="!showInsuranceValue"
                          class="ps-0 text-center"
                          style="width: 150px;"
                          :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                        >
                          Valor repasse
                        </th>
                      </template>
                    </tr>
                  </thead>
                  <tbody
                    v-for="specialty in filteredSpecialties(visibleKinds[kindIndexSelected].specialties)"
                    :key="'spec-' + specialty.id_spec"
                  >
                    <tr>
                      <td class="ps-0">
                        <span class="text-gray-800 fw-bolder text-hover-primary mb-1 fs-6 text-start pe-0">
                          {{ specialty.spec_name }}
                        </span>
                      </td>
                      <template
                        v-for="(supplierCol, sIndex) in visibleKinds[kindIndexSelected].suppliers"
                        :key="'sp-' + specialty.id_spec + '-' + supplierCol.id_supplier"
                      >
                        <td
                          v-if="showInsuranceValue"
                          class="text-center min-w-140px pe-2"
                          :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                        >
                          <money3
                            v-if="specAllowsTransferValueInput(specialty, supplierCol)"
                            class="form-control form-control-sm form-control-solid text-center"
                            v-model="supplierCellForSpec(specialty, supplierCol.id_supplier).insurance_value_place_spec"
                            v-bind="moneyConfig"
                            @focus="focusSpecTransferCell(specialty, supplierCol)"
                            @blur="updateTransferValue(specialty.id_spec, supplierCol.id_supplier)"
                          />
                          <span v-else class="text-muted fs-7">—</span>
                        </td>
                        <td
                          v-if="showInsuranceValue"
                          class="text-center min-w-140px pe-4"
                          :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                        >
                          <money3
                            v-if="specAllowsTransferValueInput(specialty, supplierCol)"
                            class="form-control form-control-sm form-control-solid text-center"
                            v-model="supplierCellForSpec(specialty, supplierCol.id_supplier).value_place_spec"
                            v-bind="moneyConfig"
                            @focus="focusSpecTransferCell(specialty, supplierCol)"
                            @blur="updateTransferValue(specialty.id_spec, supplierCol.id_supplier)"
                          />
                          <span v-else class="text-muted fs-7">—</span>
                        </td>
                        <td
                          v-if="!showInsuranceValue"
                          class="text-center min-w-140px pe-4"
                          :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                        >
                          <money3
                            v-if="specAllowsTransferValueInput(specialty, supplierCol)"
                            class="form-control form-control-sm form-control-solid text-center"
                            v-model="supplierCellForSpec(specialty, supplierCol.id_supplier).value_place_spec"
                            v-bind="moneyConfig"
                            @focus="beforeValue = supplierCellForSpec(specialty, supplierCol.id_supplier).value_place_spec"
                            @blur="
                              updateTransferValue(
                                specialty.id_spec,
                                supplierCol.id_supplier,
                                supplierCellForSpec(specialty, supplierCol.id_supplier).value_place_spec,
                              )
                            "
                          />
                          <span v-else class="text-muted fs-7">—</span>
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>

              <div v-else class="supplier-transfer-grid-wrap rounded-3 border bg-white">
                <div class="table-responsive">
                  <table class="table table-row-bordered table-row-gray-300 align-middle gs-0 gy-3 mb-0 supplier-transfer-table">
                  <thead>
                    <tr class="fs-7 fw-bolder text-gray-500 border-bottom-0">
                      <th rowspan="2" class="ps-0 min-w-200px">Exame (TUSS)</th>
                      <th
                        v-for="(supplier, sIndex) in visibleKinds[kindIndexSelected].suppliers"
                        :key="'cat-h-' + supplier.id_supplier"
                        class="text-center min-w-140px"
                        :colspan="showInsuranceValue ? 2 : 1"
                        :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                      >
                        <span
                          class="text-truncate d-inline-block max-w-125px"
                          :title="supplierNameHeaderTitle(supplier)"
                        >
                          {{ supplier.supplier_name }}
                          <span v-if="supplier.supplier_active === false" class="badge badge-sm bg-secondary ms-1">Inativo</span>
                        </span>
                      </th>
                    </tr>
                    <tr class="fs-7 fw-bolder text-gray-500 border-bottom-2">
                      <template v-for="(supplier, sIndex) in visibleKinds[kindIndexSelected].suppliers" :key="'cat-h2-' + supplier.id_supplier">
                        <th
                          v-if="showInsuranceValue"
                          class="ps-0 text-center"
                          style="width: 150px;"
                          :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                        >
                          Base convênio
                        </th>
                        <th
                          v-if="showInsuranceValue"
                          class="ps-0 text-center"
                          style="width: 150px;"
                          :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                        >
                          Valor repasse
                        </th>
                        <th
                          v-if="!showInsuranceValue"
                          class="ps-0 text-center"
                          style="width: 150px;"
                          :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                        >
                          Valor repasse
                        </th>
                      </template>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in filteredCatalogItems(visibleKinds[kindIndexSelected].items || [])"
                      :key="'item-' + item.id_item"
                    >
                      <td class="ps-0">
                        <span class="text-gray-800 fw-bolder mb-1 fs-6 text-start pe-0">
                          {{ item.label }}
                        </span>
                      </td>
                      <template
                        v-for="(supplierCol, sIndex) in visibleKinds[kindIndexSelected].suppliers"
                        :key="'it-' + item.id_item + '-' + supplierCol.id_supplier"
                      >
                        <td
                          v-if="showInsuranceValue"
                          class="text-center min-w-140px pe-2"
                          :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                        >
                          <money3
                            class="form-control form-control-sm form-control-solid text-center"
                            v-model="supplierCellForCatalogItem(item, supplierCol.id_supplier).insurance_value_place_spec"
                            v-bind="moneyConfig"
                            @focus="focusCatalogTransferCell(item, supplierCol)"
                            @blur="updateTransferValueForItem(item.id_item, supplierCol.id_supplier)"
                          />
                        </td>
                        <td
                          v-if="showInsuranceValue"
                          class="text-center min-w-140px pe-4"
                          :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                        >
                          <money3
                            class="form-control form-control-sm form-control-solid text-center"
                            v-model="supplierCellForCatalogItem(item, supplierCol.id_supplier).value_place_spec"
                            v-bind="moneyConfig"
                            @focus="focusCatalogTransferCell(item, supplierCol)"
                            @blur="updateTransferValueForItem(item.id_item, supplierCol.id_supplier)"
                          />
                        </td>
                        <td
                          v-if="!showInsuranceValue"
                          class="text-center min-w-140px pe-4"
                          :class="{ 'bg-gray-200': sIndex % 2 === 0, 'bg-gray-300': sIndex % 2 !== 0 }"
                        >
                          <money3
                            class="form-control form-control-sm form-control-solid text-center"
                            v-model="supplierCellForCatalogItem(item, supplierCol.id_supplier).value_place_spec"
                            v-bind="moneyConfig"
                            @focus="beforeValue = supplierCellForCatalogItem(item, supplierCol.id_supplier).value_place_spec"
                            @blur="
                              updateTransferValueForItem(
                                item.id_item,
                                supplierCol.id_supplier,
                                supplierCellForCatalogItem(item, supplierCol.id_supplier).value_place_spec,
                              )
                            "
                          />
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </table>
                </div>
                <div
                  v-if="!(visibleKinds[kindIndexSelected].items || []).length"
                  class="px-4 py-5 text-center text-muted border-top border-gray-200"
                >
                  Nenhum item cadastrado nesta aba. Cadastre em Exames/Procedimentos na mesma categoria
                  ({{ visibleKinds[kindIndexSelected].kind_name }}).
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import http from "../../helpers/http";
import Permission from "../../helpers/permission";
import { Money3Component } from "v-money3";
import Alert from "../../helpers/alert";
import Toolbar from "../../components/layouts/auth/Toolbar.vue";
import MultiSelect from "primevue/multiselect";

export default {
  name: "SupplierTransferValues",
  components: { money3: Money3Component, Toolbar, MultiSelect },
  data() {
    return {
      transferTables: [],
      selectedTransferTableId: null,
      showInsuranceValue: false,
      kinds: [],
      availableSuppliers: [],
      tabSuppliersSelection: [],
      tabSuppliersUpdating: false,
      kindIndexSelected: null,
      searchTerm: "",
      loaded: false,
      beforeValue: null,
      /** Snapshot ao focar célula (repasse + base convênio) para evitar PUT redundante no blur. */
      transferBlurSnapshot: null,
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    selectedTransferTableLabel() {
      const t = this.transferTables.find(
        (x) => Number(x.id_transfer_table) === Number(this.selectedTransferTableId),
      );
      return t ? t.transfer_table_name : "";
    },
    restrictTransferTabsToCatalogExamsOnly() {
      const t = this.transferTables.find(
        (x) => Number(x.id_transfer_table) === Number(this.selectedTransferTableId),
      );
      return !!(t && t.all_linked_insurances_have_id_plan === true);
    },
    visibleKinds() {
      if (!this.restrictTransferTabsToCatalogExamsOnly) {
        return this.kinds;
      }
      return this.kinds.filter(
        (k) => k.catalog_items && (k.item_type === "EXAM-LAB" || k.item_type === "EXAM-OCC"),
      );
    },
    searchPlaceholder() {
      const k = this.kindIndexSelected !== null ? this.visibleKinds[this.kindIndexSelected] : null;
      if (k && this.isCatalogKind(k)) {
        return "Pesquisar por código TUSS ou nome do exame";
      }
      return "Pesquisar exame ou procedimento";
    },
    transferTableStorageKey() {
      return `supplier_transfer_values_id_transfer_table_${this.place.id_place}`;
    },
    moneyConfig() {
      return {
        masked: false,
        prefix: "R$ ",
        suffix: "",
        thousands: ".",
        decimal: ",",
        precision: 2,
        disableNegative: true,
        disabled:
          !this.selectedTransferTableId ||
          !this.hasPermission("Update Place Supplier Transfer Value"),
        min: null,
        max: null,
        allowBlank: true,
        minimumNumberOfCharacters: 0,
        shouldRound: false,
        focusOnRight: false,
      };
    },
  },
  mounted() {
    this.loadTransferValues();
  },
  methods: {
    hasPermission(permission) {
      return Permission.has(permission);
    },
    /**
     * Célula liberada quando a API indica prof_place_spec cujo profissional tem o mesmo id_supplier da coluna.
     */
    specAllowsTransferValueInput(specialty, supplierCol) {
      const id = supplierCol && supplierCol.id_supplier;
      const cell = specialty && id != null ? specialty.suppliers.find((s) => s.id_supplier === id) : null;
      return !!(cell && cell.allows_transfer_value === true);
    },
    supplierMultiSelectLabel(opt) {
      if (!opt) {
        return "";
      }
      const n = opt.supplier_name || "";
      return opt.supplier_active === false ? `${n} (inativo)` : n;
    },
    syncTabSuppliersSelectionFromKind() {
      if (this.kindIndexSelected === null || !this.visibleKinds[this.kindIndexSelected]) {
        this.tabSuppliersSelection = [];

        return;
      }
      const k = this.visibleKinds[this.kindIndexSelected];
      const ids = new Set((k.suppliers || []).map((s) => s.id_supplier));
      this.tabSuppliersSelection = (this.availableSuppliers || []).filter((s) =>
        ids.has(s.id_supplier),
      );
    },
    kindTabKey(kind) {
      if (!kind) {
        return "";
      }
      if (kind.catalog_items && kind.item_type) {
        return "c:" + kind.item_type;
      }
      return "k:" + String(kind.id_kind ?? "");
    },
    tabSuppliersPayload(supplierId) {
      const base = {
        id_supplier: supplierId,
        id_transfer_table: this.selectedTransferTableId,
      };
      const k = this.visibleKinds[this.kindIndexSelected];
      if (!k) {
        return base;
      }
      if (k.catalog_items && k.item_type) {
        return { ...base, item_type: k.item_type };
      }
      return { ...base, id_kind: k.id_kind };
    },
    async onTabSuppliersChange(newVal) {
      if (
        this.kindIndexSelected === null ||
        !this.selectedTransferTableId ||
        !this.hasPermission("Update Place Supplier Transfer Value")
      ) {
        return;
      }
      const k = this.visibleKinds[this.kindIndexSelected];
      const serverIds = new Set((k.suppliers || []).map((s) => s.id_supplier));
      const newIds = new Set((newVal || []).map((s) => s.id_supplier));
      const toAdd = [...newIds].filter((id) => !serverIds.has(id));
      const toRemove = [...serverIds].filter((id) => !newIds.has(id));
      if (!toAdd.length && !toRemove.length) {
        return;
      }

      this.tabSuppliersUpdating = true;
      try {
        for (const id of toAdd) {
          await http.post(
            `/places/${this.place.id_place}/supplier-transfer-values/tab-suppliers`,
            this.tabSuppliersPayload(id),
          );
        }
        for (const id of toRemove) {
          await http.delete(`/places/${this.place.id_place}/supplier-transfer-values/tab-suppliers`, {
            params: this.tabSuppliersPayload(id),
          });
        }
        Alert.toastSuccess("Prestadores desta aba atualizados.");
        await this.loadTransferValues();
      } catch (e) {
        Alert.toastError(
          e.response?.data?.message ?? e.response?.data ?? "Erro ao atualizar prestadores da aba.",
        );
        this.syncTabSuppliersSelectionFromKind();
      } finally {
        this.tabSuppliersUpdating = false;
      }
    },
    resolveSelectedTransferTableId() {
      if (
        this.selectedTransferTableId &&
        this.transferTables.some(
          (t) => Number(t.id_transfer_table) === Number(this.selectedTransferTableId),
        )
      ) {
        return;
      }
      const saved = localStorage.getItem(this.transferTableStorageKey);
      if (saved && this.transferTables.some((t) => String(t.id_transfer_table) === String(saved))) {
        this.selectedTransferTableId = Number(saved);
        return;
      }
      const def = this.transferTables.find((t) => t.transfer_table_name === "Convênio Padrão");
      this.selectedTransferTableId = def
        ? def.id_transfer_table
        : this.transferTables[0]?.id_transfer_table ?? null;
      if (this.selectedTransferTableId) {
        localStorage.setItem(this.transferTableStorageKey, String(this.selectedTransferTableId));
      }
    },
    onTransferTableChange() {
      if (this.selectedTransferTableId) {
        localStorage.setItem(this.transferTableStorageKey, String(this.selectedTransferTableId));
      }
      this.loadTransferValues();
    },
    loadTransferValues() {
      const prevTabKey =
        this.kindIndexSelected !== null && this.visibleKinds[this.kindIndexSelected]
          ? this.kindTabKey(this.visibleKinds[this.kindIndexSelected])
          : null;

      this.loaded = false;

      const applyPayload = (payload) => {
        if (Array.isArray(payload)) {
          this.kinds = payload;
          this.availableSuppliers = [];
          this.showInsuranceValue = false;
        } else {
          this.kinds = payload.kinds ?? [];
          this.availableSuppliers = payload.available_suppliers ?? [];
          this.showInsuranceValue = !!payload.show_insurance_value;
          if (payload.transfer_tables) {
            this.transferTables = payload.transfer_tables;
          }
        }
        this.ensureSupplierCells();
        const tabs = this.visibleKinds;
        if (tabs.length > 0) {
          if (prevTabKey) {
            const idx = tabs.findIndex((x) => this.kindTabKey(x) === prevTabKey);
            this.kindIndexSelected = idx >= 0 ? idx : 0;
          } else {
            this.kindIndexSelected = 0;
          }
        } else {
          this.kindIndexSelected = null;
        }
        this.syncTabSuppliersSelectionFromKind();
      };

      const fetchGrid = () =>
        http
          .get(`/places/${this.place.id_place}/supplier-transfer-values`, {
            params: { id_transfer_table: this.selectedTransferTableId },
          })
          .then((response) => {
            applyPayload(response.data);
          });

      if (!this.transferTables.length) {
        return http
          .get(`/places/${this.place.id_place}/supplier-transfer-values`)
          .then((response) => {
            this.transferTables = response.data.transfer_tables || [];
            this.resolveSelectedTransferTableId();
            if (!this.selectedTransferTableId) {
              this.kinds = [];
              this.availableSuppliers = [];
              this.kindIndexSelected = null;
              return;
            }
            return fetchGrid();
          })
          .catch((e) => {
            Alert.toastError(
              e.response?.data?.message ??
                e.response?.data ??
                "Erro ao carregar valores de repasse.",
            );
          })
          .finally(() => {
            this.loaded = true;
          });
      }

      if (!this.selectedTransferTableId) {
        this.resolveSelectedTransferTableId();
        if (!this.selectedTransferTableId) {
          this.kinds = [];
          this.availableSuppliers = [];
          this.kindIndexSelected = null;
          this.loaded = true;
          return Promise.resolve();
        }
      }

      return fetchGrid()
        .catch((e) => {
          Alert.toastError(
            e.response?.data?.message ??
              e.response?.data ??
              "Erro ao carregar valores de repasse.",
          );
        })
        .finally(() => {
          this.loaded = true;
        });
    },
    /**
     * Garante um objeto por (especialidade × fornecedor) para o v-model do money3, mesmo quando o backend não envia linha.
     */
    isCatalogKind(kind) {
      return !!(kind && kind.catalog_items);
    },
    supplierNameHeaderTitle(supplier) {
      let t = supplier.supplier_name || "";
      if (supplier.supplier_active === false) {
        t += " (prestador inativo)";
      }
      return t;
    },
    ensureSupplierCells() {
      this.kinds.forEach((kind) => {
        if (!kind.specialties) {
          kind.specialties = [];
        }
        if (this.isCatalogKind(kind) && kind.items) {
          kind.items.forEach((item) => {
            (kind.suppliers || []).forEach((sup) => {
              let cell = item.suppliers.find((s) => s.id_supplier === sup.id_supplier);
              if (!cell) {
                cell = {
                  id_supplier: sup.id_supplier,
                  supplier_name: sup.supplier_name,
                  supplier_active: sup.supplier_active !== false,
                  id_suptrans_value_place_spec: null,
                  value_place_spec: null,
                  insurance_value_place_spec: null,
                };
                item.suppliers.push(cell);
              }
            });
            item.suppliers.sort((a, b) => a.id_supplier - b.id_supplier);
          });
          return;
        }
        kind.specialties.forEach((sp) => {
          kind.suppliers.forEach((sup) => {
            let cell = sp.suppliers.find((s) => s.id_supplier === sup.id_supplier);
            if (!cell) {
              cell = {
                id_supplier: sup.id_supplier,
                supplier_name: sup.supplier_name,
                supplier_active: sup.supplier_active !== false,
                allows_transfer_value: false,
                id_suptrans_value_place_spec: null,
                value_place_spec: null,
                insurance_value_place_spec: null,
              };
              sp.suppliers.push(cell);
            }
          });
          sp.suppliers.sort((a, b) => a.id_supplier - b.id_supplier);
        });
      });
    },
    setKindSelected(index) {
      this.kindIndexSelected = index;
      this.syncTabSuppliersSelectionFromKind();
    },
    filteredSpecialties(specialties) {
      if (!specialties || !this.searchTerm) {
        return specialties || [];
      }
      const q = this.searchTerm.toLowerCase();
      return specialties.filter((s) => s.spec_name.toLowerCase().includes(q));
    },
    filteredCatalogItems(items) {
      if (!items || !this.searchTerm) {
        return items || [];
      }
      const q = this.searchTerm.toLowerCase();
      return items.filter((it) => String(it.label || "").toLowerCase().includes(q));
    },
    supplierCellForSpec(specialty, supplierId) {
      let cell = specialty.suppliers.find((s) => s.id_supplier === supplierId);
      if (!cell) {
        cell = {
          id_supplier: supplierId,
          supplier_name: "",
          allows_transfer_value: false,
          id_suptrans_value_place_spec: null,
          value_place_spec: null,
          insurance_value_place_spec: null,
        };
        specialty.suppliers.push(cell);
      }
      return cell;
    },
    supplierCellForCatalogItem(item, supplierId) {
      let cell = item.suppliers.find((s) => s.id_supplier === supplierId);
      if (!cell) {
        cell = {
          id_supplier: supplierId,
          supplier_name: "",
          id_suptrans_value_place_spec: null,
          value_place_spec: null,
          insurance_value_place_spec: null,
        };
        item.suppliers.push(cell);
      }
      return cell;
    },
    focusSpecTransferCell(specialty, supplierCol) {
      const cell = this.supplierCellForSpec(specialty, supplierCol.id_supplier);
      this.transferBlurSnapshot = {
        kind: "spec",
        idSpec: specialty.id_spec,
        idSupplier: supplierCol.id_supplier,
        value: cell.value_place_spec,
        insurance: cell.insurance_value_place_spec,
      };
    },
    focusCatalogTransferCell(item, supplierCol) {
      const cell = this.supplierCellForCatalogItem(item, supplierCol.id_supplier);
      this.transferBlurSnapshot = {
        kind: "item",
        idItem: item.id_item,
        idSupplier: supplierCol.id_supplier,
        value: cell.value_place_spec,
        insurance: cell.insurance_value_place_spec,
      };
    },
    updateTransferValueForItem(itemId, supplierId, value) {
      if (!this.selectedTransferTableId) {
        return;
      }
      const catalogRow = (this.visibleKinds[this.kindIndexSelected].items || []).find((x) => x.id_item === itemId);
      const cell = catalogRow ? this.supplierCellForCatalogItem(catalogRow, supplierId) : null;
      if (this.showInsuranceValue && cell) {
        const snap = this.transferBlurSnapshot;
        if (
          snap &&
          snap.kind === "item" &&
          snap.idItem === itemId &&
          snap.idSupplier === supplierId &&
          snap.value === cell.value_place_spec &&
          snap.insurance === cell.insurance_value_place_spec
        ) {
          return;
        }
        http
          .put(`/places/${this.place.id_place}/supplier-transfer-values`, {
            id_transfer_table: this.selectedTransferTableId,
            id_supplier: supplierId,
            id_item: itemId,
            value: cell.value_place_spec,
            insurance_value: cell.insurance_value_place_spec,
          })
          .then(() => Alert.toastSuccess("Valor de repasse atualizado."))
          .catch((e) => {
            Alert.toastError(e.response?.data?.message ?? e.response?.data ?? "Erro ao salvar.");
            this.loadTransferValues();
          });
        return;
      }
      if (this.beforeValue === value) {
        return;
      }
      http
        .put(`/places/${this.place.id_place}/supplier-transfer-values`, {
          id_transfer_table: this.selectedTransferTableId,
          id_supplier: supplierId,
          id_item: itemId,
          value,
        })
        .then(() => Alert.toastSuccess("Valor de repasse atualizado."))
        .catch((e) => {
          Alert.toastError(e.response?.data?.message ?? e.response?.data ?? "Erro ao salvar.");
          this.loadTransferValues();
        });
    },
    updateTransferValue(specId, supplierId, value) {
      if (!this.selectedTransferTableId) {
        return;
      }
      const specialty = (this.visibleKinds[this.kindIndexSelected].specialties || []).find(
        (s) => s.id_spec === specId,
      );
      if (!this.specAllowsTransferValueInput(specialty, { id_supplier: supplierId })) {
        return;
      }
      const cell = specialty ? this.supplierCellForSpec(specialty, supplierId) : null;
      if (this.showInsuranceValue && cell) {
        const snap = this.transferBlurSnapshot;
        if (
          snap &&
          snap.kind === "spec" &&
          snap.idSpec === specId &&
          snap.idSupplier === supplierId &&
          snap.value === cell.value_place_spec &&
          snap.insurance === cell.insurance_value_place_spec
        ) {
          return;
        }
        http
          .put(`/places/${this.place.id_place}/supplier-transfer-values`, {
            id_transfer_table: this.selectedTransferTableId,
            id_supplier: supplierId,
            id_spec: specId,
            value: cell.value_place_spec,
            insurance_value: cell.insurance_value_place_spec,
          })
          .then(() => Alert.toastSuccess("Valor de repasse atualizado."))
          .catch((e) => {
            Alert.toastError(e.response?.data?.message ?? e.response?.data ?? "Erro ao salvar.");
            this.loadTransferValues();
          });
        return;
      }
      if (this.beforeValue === value) {
        return;
      }
      http
        .put(`/places/${this.place.id_place}/supplier-transfer-values`, {
          id_transfer_table: this.selectedTransferTableId,
          id_supplier: supplierId,
          id_spec: specId,
          value,
        })
        .then(() => Alert.toastSuccess("Valor de repasse atualizado."))
        .catch((e) => {
          Alert.toastError(e.response?.data?.message ?? e.response?.data ?? "Erro ao salvar.");
          this.loadTransferValues();
        });
    },
  },
};
</script>

<style scoped>
.max-w-125px {
  max-width: 125px;
}

.supplier-transfer-page :deep(.table thead th) {
  vertical-align: middle;
}

.supplier-transfer-multiselect :deep(.p-multiselect) {
  width: 100%;
}
</style>
